import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { saveWorkingProjectByFields, saveWorkingProjectSessionByField } from '@/store/slices/project';
import { Session } from '@/types/session.types';
import {
  selectIsSingleSession,
  selectOriginatingTemplateName,
  selectWorkingProjectImage,
  selectWorkingProjectRegistrationSettings,
} from '@/store/slices/project/selectors';
import ProjectImageCarousel from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/project-image-carousel';
import ProjectImage from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/project-image';
import { TemplateNames } from '@/types/template-layouts.types';
import ProjectImageFooterContent from '../edit-project-image/project-image-footer-content';

type EditSessionImageProps = {
  session: Session;
  height?: string;
  width?: string;
};

const EditSessionImage = ({ session }: EditSessionImageProps) => {
  const sessionImage = session.pre_broadcast_thumbnail;
  const originatingTemplateName = useAppSelector(selectOriginatingTemplateName);
  const workingProjectImage = useAppSelector(selectWorkingProjectImage);
  const regSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const isSingleSession = useAppSelector(selectIsSingleSession);
  const [uploadedProjectImage, setUploadedProjectImage] = useState<string | null | undefined>(workingProjectImage);
  const [uploadedSessionOrBannerImage, setUploadedSessionOrBannerImage] = useState<string | null | undefined>(
    sessionImage
  );
  const [uploadedRegistrationImage, setUploadedRegistrationImage] = useState<string | undefined>(regSettings?.image);
  const dispatch = useAppDispatch();

  const onUpload = async (key: string, url: string | null) => {
    if (!session) return;

    // See https://brandlive.atlassian.net/browse/BDEV-2278 for AC details
    switch (key) {
      case 'projectImage': {
        setUploadedProjectImage(url);
        dispatch(saveWorkingProjectByFields({ event_image: url || undefined }));

        // if single session, match what shows in the session overview modal
        if (isSingleSession) {
          dispatch(
            saveWorkingProjectSessionByField({
              uuid: session.uuid,
              image: url,
            })
          );
        }
        return;
      }
      case 'sessionOrBannerImage': {
        setUploadedSessionOrBannerImage(url);

        dispatch(
          saveWorkingProjectSessionByField({
            uuid: session.uuid,
            pre_broadcast_thumbnail: url,
          })
        );

        return;
      }
      case 'registrationImage': {
        setUploadedRegistrationImage(url ?? undefined);
        dispatch(
          saveWorkingProjectByFields({
            registration_settings: regSettings
              ? {
                  ...regSettings,
                  image: url ?? undefined,
                }
              : undefined,
          })
        );
        return;
      }
    }
  };

  if (!isSingleSession) {
    return (
      <ProjectImage
        img={sessionImage}
        height="220px"
        width="392px"
        FooterContent={
          !originatingTemplateName || originatingTemplateName === TemplateNames.Limelight
            ? null
            : () => <ProjectImageFooterContent label={originatingTemplateName} />
        }
        onUpload={async (url: string) => {
          setUploadedSessionOrBannerImage(url);
          dispatch(
            saveWorkingProjectSessionByField({
              uuid: session.uuid,
              image: url,
              pre_broadcast_thumbnail: url,
            })
          );
        }}
        onDelete={() => {
          setUploadedSessionOrBannerImage(null);
          dispatch(
            saveWorkingProjectSessionByField({
              uuid: session.uuid,
              image: null,
              pre_broadcast_thumbnail: null,
            })
          );
        }}
      />
    );
  }

  return (
    <ProjectImageCarousel
      onUpload={onUpload}
      onDelete={(key: string) => onUpload(key, null)}
      images={{
        projectImage: uploadedProjectImage,
        registrationImage: uploadedRegistrationImage,
        sessionOrBannerImage: uploadedSessionOrBannerImage,
      }}
    />
  );
};

export default EditSessionImage;
